/**
 * @file Services.jsx
 * @description Services page with Tailwind CSS
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import IMAGES from '../config/images';
import { pricingData } from '../data/pricing';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [
    {
      category: 'Plumbing Services',
      icon: '🔧',
      gradient: 'from-blue-500 to-blue-600',
      image: IMAGES.plumbingServices,
      description: 'Expert plumbing solutions for homes and businesses',
      items: [
        'Leaking taps and pipes',
        'Toilet repairs and installations',
        'Basin and sink installations',
        'Shower installations and repairs',
        'Bath installations',
        'Pipe repairs and replacements',
        'Drain unblocking',
        'Water pressure issues',
        'General plumbing maintenance'
      ]
    },
    {
      category: 'Gas Services',
      icon: '🔥',
      gradient: 'from-orange-500 to-red-600',
      image: IMAGES.gasServices,
      description: 'Gas Safe registered engineers for all gas work',
      items: [
        'Gas Safety Certificates (CP12)',
        'Gas appliance installations',
        'Gas cooker installations',
        'Gas hob installations',
        'Gas leak detection and repair',
        'Gas pipe installations',
        'Gas meter relocations',
        'LPG conversions',
        'Carbon monoxide testing'
      ]
    },
    {
      category: 'Heating Services',
      icon: '♨️',
      gradient: 'from-red-500 to-pink-600',
      image: IMAGES.heatingServices,
      description: 'Complete heating system solutions',
      items: [
        'Boiler installations',
        'Boiler servicing',
        'Boiler repairs',
        'Central heating systems',
        'Radiator installations',
        'Powerflushing',
        'Underfloor heating',
        'Thermostatic controls',
        'Heating system upgrades'
      ]
    },
    {
      category: 'Emergency Services',
      icon: '🚨',
      gradient: 'from-red-600 to-red-700',
      image: IMAGES.emergencyServices,
      description: '24/7 emergency callout service',
      items: [
        'Emergency leak repairs',
        'Boiler breakdowns',
        'No hot water emergencies',
        'Gas leak emergencies',
        'Burst pipe repairs',
        'Blocked drain emergencies',
        'Heating failures',
        '24/7 availability',
        'Rapid response times'
      ]
    }
  ];

  const whyChooseUs = [
    { icon: '🏆', title: 'Gas Safe Registered', desc: 'All our engineers are fully qualified and Gas Safe registered' },
    { icon: '⚡', title: 'Fast Response', desc: '24/7 emergency service with rapid response times' },
    { icon: '💰', title: 'Competitive Rates', desc: 'Transparent pricing with no hidden charges' },
    { icon: '🛡️', title: 'Fully Insured', desc: 'Comprehensive insurance for your peace of mind' },
    { icon: '⭐', title: 'Quality Work', desc: 'High standards of workmanship on every job' },
    { icon: '💬', title: 'Excellent Service', desc: 'Outstanding customer reviews and satisfaction' }
  ];

  return (
    <div className="services-page">
      <Helmet>
        <title>Services | Metro Plumbing & Heating</title>
        <meta
          name="description"
          content="Professional plumbing, gas, and heating services in London. Gas Safe registered engineers. 24/7 emergency callouts available."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-white rounded-full top-0 -right-20 animate-pulse-slow"></div>
          <div className="absolute w-64 h-64 bg-white rounded-full bottom-0 left-20 animate-bounce-slow"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-blue-100">
              Professional plumbing, gas, and heating services across London
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="relative group overflow-hidden rounded-2xl shadow-xl">
                    <img
                      src={service.image}
                      alt={service.category}
                      className="w-full h-96 object-cover transform group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = IMAGES.servicePlaceholder;
                      }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-0 group-hover:opacity-0 transition-opacity duration-300`}></div>
                    {/* Decorative corner badge */}
                    <div className="absolute top-4 right-4 bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm font-semibold capitalize">{service.category.split(' ')[0]}</div>
                    <div className="absolute bottom-8 left-8 text-white">
                      <div className="text-6xl mb-4 animate-bounce-slow">{service.icon}</div>
                    </div>
                  </div>
                </div>
                <div className={index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{service.category}</h2>
                  <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3 group">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mt-0.5 relative">
                          <span className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-0 group-hover:opacity-75"></span>
                          <span className="relative">✓</span>
                        </span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of customers across London
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="card text-center p-8 animate-fade-in-up relative overflow-hidden group hover:scale-105 transition-transform duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute top-0 right-0 w-16 h-16 bg-primary-100 rounded-full opacity-0 group-hover:opacity-100 -mr-8 -mt-8 transition-all duration-500"></div>
                <div className="text-5xl mb-4 animate-pulse-slow relative z-10">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Rates</h2>
            <p className="text-lg text-gray-600">Transparent, competitive pricing for all services</p>
            <p className="text-sm text-primary-600 font-semibold mt-2">Zone 1 locations: Add £30 to all prices</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Standard Rates */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up">
              <h3 className="text-xl font-bold text-blue-900 mb-4 pb-3 border-b-2 border-blue-300">Standard Rates</h3>
              <div className="space-y-3">
                {pricingData.standard.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service}</span>
                    <span className="text-blue-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Evening Rates */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3 className="text-xl font-bold text-purple-900 mb-4 pb-3 border-b-2 border-purple-300">Evening (6pm-10pm)</h3>
              <div className="space-y-3">
                {pricingData.evening.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace(' (6pm-10pm)', '')}</span>
                    <span className="text-purple-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekend Rates */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h3 className="text-xl font-bold text-green-900 mb-4 pb-3 border-b-2 border-green-300">Weekend</h3>
              <div className="space-y-3">
                {pricingData.weekend.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace('Weekend ', '').replace('Plumbing', 'Plumbing (Sat)').replace('Gas', 'Gas (Sat)')}</span>
                    <span className="text-green-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Emergency Rates */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up border-2 border-red-300" style={{ animationDelay: '0.3s' }}>
              <h3 className="text-xl font-bold text-red-900 mb-4 pb-3 border-b-2 border-red-300">Emergency / Special</h3>
              <div className="space-y-3">
                {pricingData.emergency.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{item.service.replace(' (10pm-7am)', '')}</span>
                    <span className="text-red-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Link
              to="/get-quote"
              className="inline-block px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in-up">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Contact us today for a free quote or emergency assistance
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <a
              href="tel:+447379787766"
              className="px-8 py-4 bg-white text-primary-600 rounded-lg font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              📞 Call: 07379 787766
            </a>
            <Link
              to="/get-quote"
              className="px-8 py-4 bg-danger-600 text-white rounded-lg font-bold text-lg shadow-xl hover:bg-danger-700 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
